<template>
  <section>
    <div class="flex items-center justify-between">
      <div class="mb-5 flex items-center gap-4">
        <app-single-select
          id="selectedYear"
          label="Year"
          v-model="selectedYear"
          :options="yearOptions"
          class="!w-[120px]"
        >
        </app-single-select>
        <app-single-select
          v-if="filterBy === 'monthly' && selectedYear !== 'All'"
          id="selectedMonth"
          label="Month"
          v-model="selectedMonth"
          :options="monthsDropdown"
          class="!w-[180px]"
          @update:modelValue="handleMonthChange"
        >
        </app-single-select>
        <app-single-select
          id="currency"
          label="Currency"
          :options="currencies"
          v-model="selectedCurrency"
          class="!w-[120px]"
        ></app-single-select>
        <app-single-select
          v-if="selectedYear !== 'All'"
          id="expenseType"
          label="Expense Type"
          :options="cashOrBank"
          v-model="selectedType"
          class="!w-[120px]"
        ></app-single-select>
      </div>
      <div class="flex items-center gap-4">
        <app-secondary-button
          v-if="filterBy === 'monthly' || selectedYear?.toLowerCase() !== 'all'"
          :button-label="
            filterBy === 'monthly'
              ? $t('Back to monthly overview'.getTranslationKey())
              : $t('Total overview'.getTranslationKey())
          "
          @click="handleBackNavigation"
        ></app-secondary-button>
        <app-single-select
          v-if="filterBy === 'monthly'"
          :show-selected="false"
          label-placeholder="More actions"
          v-model="selectedAction"
          :options="actions"
          class="!w-[200px]"
        ></app-single-select>
      </div>
    </div>
    <div v-if="loading" class="h-[500px] flex justify-center items-center">
      <app-loader></app-loader>
    </div>
    <app-custom-table
      v-else
      double-header
      :top-headers="tableHeaders.top"
      :headers="tableHeaders.bottom"
      scroll-horizontally
      :items="isAllProjectOverview"
      :column-widths="{
        budget__budget_item__position: '200px',
      }"
      :row-styles="() => '!static border-b'"
      :column-styles="() => 'border-l'"
      :currency-symbol="this.selectedCurrency"
      :show-currency="false"
    >
      <template
        v-for="headerSlot in yearlyHeaderSlots"
        :key="headerSlot"
        v-slot:[headerSlot]="{ item }"
      >
        <p
          :class="['cursor-pointer', getMonthStyles(item)]"
          @click="viewHeaderDetails(item)"
        >
          {{ selectedYear === "All" ? item : $t(item.getTranslationKey()) }}
        </p>
      </template>
      <template
        v-for="headerSlot in totalHeaderSlots"
        :key="headerSlot"
        v-slot:[headerSlot]="{ item }"
      >
        <p>{{ $t(item.getTranslationKey()) }} ({{ selectedCurrency }})</p>
      </template>
    </app-custom-table>
    <additional-info
      ref="additionalInfo"
      :year="selectedYear"
      :month="selectedMonth"
      @reset-action="selectedAction = $event"
      :currency="selectedCurrency"
      @get-yearly-data="filterBy = 'yearly'"
    ></additional-info>
  </section>
</template>

<script>
import {
  formatAmount,
  monthNames,
  monthsNameWithNumber,
} from "@/services/helpers";
import Api from "@/services/api";
import { currencyBudgetPosition } from "@/services/endpoints";
import AdditionalInfo from "@/components/dashboard/projects/pm-local/projectOverview/AdditionalInfo.vue";
export default {
  name: "ProjectOverviewList",
  components: { AdditionalInfo },
  data() {
    return {
      cash: "",
      bank: "",
      filterBy: "all",
      selectedYear: "All",
      selectedAction: null,
      headers: [],
      monthlyTopHeaders: [
        {
          key: "title",
          title: "Title",
          rowspan: 2,
          position: 0,
        },
        {
          key: "document_date",
          title: "Document Date",
          type: "date",
          rowspan: 2,
          position: 1,
        },
        {
          key: "payment_date",
          title: "Payment Date",
          rowspan: 2,
          position: 2,
          type: "date",
        },
        { key: "supplier", title: "Supplier", rowspan: 2, position: 3 },
        {
          key: "currency__currency",
          title: "Currency",
          rowspan: 2,
          position: 4,
        },
        { key: "amount", title: "Amount", colspan: 2 },
        { key: "eur_amount", title: "EUR Amount", colspan: 2 },
        // { key: "info", title: "Info", rowspan: 2, position: 9 },
        // {
        //   key: "budget_position",
        //   title: "Budget Position",
        //   rowspan: 2,
        //   position: 10,
        // },
      ],
      monthlyBottomHeaders: [
        { key: "total_amount_taxed", label: "Tax Amount", type: "currency" },
        {
          key: "total_amount_not_taxed",
          label: "Untaxed Amount",
          type: "currency",
        },
        {
          key: "total_amount_taxed_eur",
          label: "Tax Amount",
          type: "currency",
        },
        {
          key: "total_amount_not_taxed_eur",
          label: "Untaxed Amount",
          type: "currency",
        },
      ],
      individualMonthHeaders: [
        "Budget Item",
        "Doc. Date",
        "Pay Date",
        "Supplier",
        "Currency",
        "Files",
        "Amt Excl. Tax",
        "Amt Incl. Tax",
        "Amt(EUR) Excl. Tax",
        "Amt(EUR) Incl. Tax",
      ],
      monthlyHeaders: [
        "Budget Item",
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
        "Year's Total",
      ],
      TotalHeaders: [
        "Budget Item",
        "Quantity",
        "Unit Amt",
        "Plan Total",
        "Plan Monthly",
        "Yearly",
        "Total",
        "Remaining Amt",
      ],
      currencies: [],
      cashOrBank: [
        { text: "Cash", value: "cash" },
        { text: "Bank", value: "bank" },
        { text: "Bank and cash", value: "all" },
      ],
      selectedCurrency: null,
      selectedType: "cash",
      selectedMonth: null,
      isFirstLoad: true,
      loading: false,
      projectYears: [],
    };
  },
  watch: {
    isOverViewStartDate: {
      handler(val) {
        if (val) {
          const currentYear = new Date().getFullYear();
          const startYear = new Date(this.isOverViewStartDate).getFullYear();
          const endYear = new Date(this.isOverViewEndDate).getFullYear();

          if (currentYear < startYear) {
            this.selectedYear = startYear.toString();
          } else if (currentYear > endYear) {
            this.selectedYear = endYear.toString();
          }

          this.selectedMonth = new Intl.DateTimeFormat("en-US", {
            month: "long",
          })?.format(new Date());
          this.$emit("lock-data", {
            month: this.selectedMonth,
            year: this.selectedYear || new Date().getFullYear(),
          });
        }
      },
      immediate: true,
    },
    selectedAction: function (val) {
      if (
        val === "lock" ||
        val === "unlock" ||
        val === "approve" ||
        val === "disapprove"
      ) {
        this.$refs.additionalInfo.openLockMonth(val);
      } else if (val) {
        this.$refs.additionalInfo.openActionTablesModal(val);
      }
    },
    filterBy: function (val) {
      if (this.isFirstLoad) return;
      const lowerCaseVal = val.toLowerCase();
      if (lowerCaseVal === "all") {
        this.getAllPmLocalOverviews({
          load_page: false,
          currency: this.selectedCurrency,
        });
      } else if (lowerCaseVal === "yearly") {
        this.getYearlyPMLocalData({
          year: this.selectedYear,
          currency: this.selectedCurrency,
          expense_type: this.selectedType,
        });
      } else {
        this.getIndividualMonthPmLocalOverview({
          year: this.selectedYear,
          currency: this.selectedCurrency,
          expense_type: this.selectedType,
          month: this.selectedMonth,
        });
      }
      this.$emit("update:filterBy", val);
    },
    selectedCurrency: function () {
      if (this.isFirstLoad) return;
      if (this.filterBy.toLowerCase() === "monthly") {
        this.getIndividualMonthPmLocalOverview({
          year: this.selectedYear,
          currency: this.selectedCurrency,
          expense_type: this.selectedType,
          month: this.selectedMonth,
        });
      } else if (
        this.filterBy.toLowerCase() === "yearly" ||
        this.selectedYear?.toLowerCase() !== "all"
      ) {
        this.getYearlyPMLocalData({
          year: this.selectedYear,
          currency: this.selectedCurrency,
          expense_type: this.selectedType,
        });
      } else {
        this.getAllPmLocalOverviews({
          load_page: false,
          currency: this.selectedCurrency,
        });
      }
    },
    selectedType: function (val) {
      if (this.isFirstLoad) return;
      if (this.filterBy.toLowerCase() === "monthly") {
        this.getIndividualMonthPmLocalOverview({
          year: this.selectedYear,
          currency: this.selectedCurrency,
          expense_type: val,
          month: this.selectedMonth,
        });
      } else {
        this.getYearlyPMLocalData({
          year: this.selectedYear,
          currency: this.selectedCurrency,
          expense_type: val,
        });
      }
    },
    selectedYear: function (val) {
      if (this.isFirstLoad) return;
      if (val === "All") {
        if (this.filterBy.toLowerCase() === "all") {
          this.getAllPmLocalOverviews({
            load_page: false,
            currency: this.selectedCurrency,
          });
        } else {
          this.filterBy = "all";
        }
      } else {
        this.getYearlyPMLocalData({
          year: val,
          currency: this.selectedCurrency,
          expense_type: this.selectedType,
        });
      }
    },
  },
  computed: {
    yearlyBottomHeaders() {
      const projectStartDate = new Date(this.isOverViewStartDate);
      const projectStartMonth = projectStartDate.getMonth();
      const projectStartYear = projectStartDate.getFullYear();

      if (this.selectedYear === projectStartYear.toString()) {
        return monthsNameWithNumber.slice(projectStartMonth).map((month) => ({
          key: month.text.toLowerCase(),
          label: month.text,
          type: "currency",
        }));
      }

      return monthsNameWithNumber.map((month) => ({
        key: month.text.toLowerCase(),
        label: month.text,
        type: "currency",
      }));
    },
    isAllBalanceInfoData() {
      return this.$store.getters["projectOverview/isAllBalanceInfoData"];
    },
    actions() {
      const lockingAndApprovingInfo = this.getMonthLockingAndApprovingInfo(
        this.selectedMonth
      );
      return [
        {
          value: "lock",
          text: "Lock",
        },
        { value: "bank_cash_transfer", text: "Bank/Cash Transfer" },
        { value: "currency_exchange", text: "Currency exchange" },
        { value: "average_exchange_rate", text: "Average exchange rate" },
        { value: "fund_expense_overview", text: "Fund expense overview" },
      ].filter((action) => {
        if (action.value === "lock")
          return (
            !lockingAndApprovingInfo?.is_locked &&
            this.isAllBalanceInfoData?.can_lock_month !== false
          );
        return true;
      });
    },
    yearOptions() {
      const yearsFromProject = this.projectYears.map((year) => ({
        text: year?.toString(),
        value: year?.toString(),
      }));
      return [{ text: "All", value: "All" }, ...yearsFromProject];
    },
    monthsDropdown() {
      return monthNames?.map((month) => ({ value: month, text: month }));
    },
    totalHeaderSlots() {
      return ["header-plan_amount", "header-total", "header-remaining_amount"];
    },
    totalTopHeaders() {
      const projectYearsLength = this.yearOptions?.filter(
        (year) => year.value !== "All"
      ).length;
      return [
        {
          key: "budget__budget_item__position",
          title: "Title",
          rowspan: 2,
          position: 0,
        },
        // { key: "budget_quantity", title: "Quantity", rowspan: 2, position: 1 },
        { key: "plan_amount", title: "Plan", colspan: 2 },
        { key: "total", title: "Total", colspan: projectYearsLength + 2 },
        {
          key: "remaining_amount",
          title: "Remaining",
          rowspan: 2,
          position: projectYearsLength + 5,
        },
      ];
    },
    totalBottomHeaders() {
      const projectYearsHeaders = this.yearOptions
        ?.filter((year) => year.value !== "All")
        .map((year) => ({
          key: year?.value,
          label: year?.text,
          type: "currency",
          noTranslation: true,
        }));
      return [
        { key: "plan_total", label: "Total", type: "currency" },
        { key: "plan_monthly", label: "Plan Monthly", type: "currency" },
        ...projectYearsHeaders,
        { key: "checks", label: "Checks", type: "currency" },
        { key: "total_eur", label: "Total", type: "currency" },
      ];
    },
    yearlyTopHeaders() {
      return [
        {
          key: "budget__budget_item__position",
          title: "Title",
          rowspan: 2,
          position: 0,
        },
        {
          key: "total_eur",
          title: this.$t("projects.pmLocalYearlyTotalHeader", {
            currency: this.selectedCurrency,
            year: this.selectedYear,
          }),
          colspan: this.yearlyBottomHeaders.length,
          noTranslation: true,
        },
        {
          key: "yearly_total",
          title: "Total",
          rowspan: 2,
          position: this.yearlyBottomHeaders.length + 1,
        },
      ];
    },
    tableHeaders() {
      if (this.selectedYear === "All")
        return { top: this.totalTopHeaders, bottom: this.totalBottomHeaders };
      return this.filterBy === "monthly"
        ? { top: this.monthlyTopHeaders, bottom: this.monthlyBottomHeaders }
        : { top: this.yearlyTopHeaders, bottom: this.yearlyBottomHeaders };
    },
    yearlyHeaderSlots() {
      if (this.selectedYear === "All")
        return this.yearOptions
          .filter((year) => year.value !== "All")
          .map((year) => `header-${year.value}`);
      return this.yearlyBottomHeaders?.map((header) => `header-${header.key}`);
    },
    filterOptions() {
      return [
        { text: "Total", value: "Total" },
        { text: "Yearly", value: "Yearly" },
        { text: "Monthly", value: "Monthly" },
      ];
    },
    lockingAndApprovingInfo() {
      return this.$store.getters["projectOverview/getLockingAndApprovingInfo"];
    },

    isAllProjectOverview() {
      return this.$store.getters["projectOverview/isAllOverviews"];
    },
    isTotalsOverview() {
      return this.$store.getters["projectOverview/isTotalsOverview"];
    },
    isForeignEstablishmentId() {
      return this.$store.getters["pmLocalModules/isForeignEstablishmentID"];
    },
    isProjectId() {
      return this.$store.getters["pmLocalModules/isProjectId"];
    },
    isOverViewStartDate() {
      return this.$store.getters["projectOverview/isOverViewStartDate"];
    },
    isOverViewEndDate() {
      return this.$store.getters["projectOverview/isOverViewEndDate"];
    },

    isProcessedObject() {
      let years = [];
      this.isAllProjectOverview.forEach((item) => {
        if (item.yearly_data) {
          years = Object.keys(item.yearly_data);
        }
      });
      years = [...new Set(years)].sort((a, b) => a - b);
      this.modifyHeaders(this.TotalHeaders, years);

      return this.isAllProjectOverview.map((item) => {
        let formatter = this.flattenYears(item);
        const formatted_yearly_data = {};
        Object.keys(item.yearly_data || {}).forEach((year) => {
          formatted_yearly_data[year] = this.amountFormatting(
            item.yearly_data[year]
          );
        });

        return { ...formatter, ...formatted_yearly_data };
      });
    },
    tableItems() {
      if (this.headers === this.TotalHeaders) {
        return this.isProcessedObject;
      } else if (this.headers === this.monthlyHeaders) {
        return this.isAllProjectOverview;
      } else {
        return this.isAllProjectOverview;
      }
    },
    canEditPersonalModule() {
      return this.$store.state.auth.canEditModuleSections;
    },
    isEditButton() {
      return (
        this.$store.getters["hrResources/isEditButton"] &&
        this.canEditPersonalModule?.includes("Expenses")
      );
    },
    isEditPmLocal() {
      return this.$store.getters["hrResources/isEditEmployee"];
    },
    currentPath() {
      return this.$route.path;
    },
    shouldDisableInput() {
      // Add your condition to disable the input element here
      return (
        this.currentPath === "/edit-pm-local" &&
        this.isEditPmLocal &&
        !this.isEditButton
      );
    },
  },
  methods: {
    handleBackNavigation() {
      if (this.filterBy.toLowerCase() === "monthly") {
        this.filterBy = "yearly";
      } else {
        this.selectedYear = "All";
      }
    },
    handleMonthChange() {
      if (this.selectedMonth) {
        this.getIndividualMonthPmLocalOverview({
          year: this.selectedYear,
          currency: this.selectedCurrency,
          expense_type: this.selectedType,
          month: this.selectedMonth,
        });
      }
    },
    getMonthLockingAndApprovingInfo(header) {
      if (this.selectedYear === "All") {
        const yearItem = this.$store.getters[
          "projectOverview/getTotalLockingAndApprovingInfo"
        ]?.find((item) => item.year?.toString() === header);
        return yearItem || null;
      }
      const monthNumber = monthsNameWithNumber.find(
        (month) => month.text === header
      )?.value;

      return this.lockingAndApprovingInfo[monthNumber] || null;
    },
    getMonthStyles(month) {
      const lockingAndApprovalInfo =
        this.getMonthLockingAndApprovingInfo(month);
      if (!lockingAndApprovalInfo) return "";
      if (
        lockingAndApprovalInfo.is_locked &&
        lockingAndApprovalInfo.is_approved
      ) {
        return "font-semibold hover:text-gray-900";
      } else if (lockingAndApprovalInfo.is_locked) {
        return "text-gray-400 hover:text-gray-500";
      } else {
        return "text-blue-500 hover:text-blue-600";
      }
    },
    viewHeaderDetails(item) {
      if (this.selectedYear === "All") {
        this.selectedYear = item;
      } else {
        this.selectedMonth = item;
        this.filterBy = "monthly";
      }
    },
    handleFilterChange(newValue) {
      this.$emit("filter-change", newValue);
    },
    amountFormatting(amt) {
      return formatAmount(amt);
    },
    getYearsFromProject(startDate, endDate) {
      const startYear = new Date(startDate).getFullYear();
      const endYear = new Date(endDate).getFullYear();
      const years = [];
      for (let i = startYear; i <= endYear; i++) {
        years.push({ text: i.toString(), value: i.toString() });
      }
      return years;
    },

    async getAllPmLocalOverviews(data) {
      try {
        this.loading = true;
        const payload = {
          load_page: data?.load_page,
          projectId: this.isProjectId,
          foreignEstablishmentId: this.isForeignEstablishmentId,
          currency: this.selectedCurrency,
        };
        await this.$store.dispatch(
          "projectOverview/getAllPmLocalOverviews",
          payload
        );
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async getYearlyPMLocalData() {
      try {
        this.loading = true;
        await this.$store.dispatch("projectOverview/getPmLocalYearOverviews", {
          projectId: this.isProjectId,
          foreignEstablishmentId: this.isForeignEstablishmentId,

          currency: this.selectedCurrency,
          year: this.selectedYear,
          expense_type: this.selectedType,
        });
      } catch (error) {
        console.error("Error:::", error);
      } finally {
        this.loading = false;
      }
    },
    async getIndividualMonthPmLocalOverview(data) {
      try {
        this.loading = true;
        await this.$store.dispatch(
          "projectOverview/getPmLocalMonthlyOverviews",
          {
            projectId: this.isProjectId,
            foreignEstablishmentId: this.isForeignEstablishmentId,
            ...data,
          }
        );
      } catch (e) {
        console.error("error:::", e);
      } finally {
        this.loading = false;
      }
    },
    flattenYears(itemOrArray) {
      const flattenItem = (obj) => {
        if (obj.yearly_data) {
          const flattened = { ...obj, ...obj.yearly_data };
          delete flattened.yearly_data;
          return flattened;
        }
        return obj;
      };

      if (Array.isArray(itemOrArray)) {
        return itemOrArray.map((item) => flattenItem(item));
      } else {
        return flattenItem(itemOrArray);
      }
    },

    modifyHeaders(headers, yearsArray) {
      const index = headers.indexOf("Yearly");
      if (index !== -1) {
        headers.splice(index, 1, ...yearsArray);
      }
      return headers;
    },
    async getResources() {
      try {
        const yearResponse = await Api().get(
          `/projects/project/pm-local/get-project-years/${this.isForeignEstablishmentId}`
        );
        if (yearResponse.data.status === "success") {
          this.projectYears = yearResponse.data.data || [];
        }

        const response = await Api().get(
          `${currencyBudgetPosition}3/${this.isForeignEstablishmentId}`
        );
        if (response.data.status === "success") {
          this.currencies = response.data.data?.map((item) => ({
            value: item?.currency,
            text: item?.currency,
          }));
          this.$store.commit(
            "projectOverview/SET_DEFAULT_CURRENCY_TO_STATE",
            this.currencies[0]?.text
          );
          this.selectedCurrency = this.currencies[0]?.text;

          if (this.selectedYear?.toLowerCase() === "all") {
            await this.getAllPmLocalOverviews();
          } else {
            await this.getYearlyPMLocalData({
              year: this.selectedYear,
              currency: this.selectedCurrency,
              expense_type: this.selectedType,
            });
          }
          this.isFirstLoad = false;
        }
      } catch (error) {
        console.error(error);
      }
    },
    closeLockMonth() {
      this.selectedAction = null;
      this.$refs.dialog.showDialog = false;
    },
  },
  mounted() {
    this.getResources();
  },
  created() {
    this.headers = this.TotalHeaders;
  },
};
</script>

<style scoped>
input[type="radio"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border: 1px solid #d0d5dd;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  outline: none;
  margin-right: 0.5rem;
  position: relative;
  cursor: pointer;
}

/* middle fill color */
input[type="radio"]::before {
  content: "";
  position: absolute;
  top: 51%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background-color: white;
  border-radius: 50%;
}

/* selected radio button */
input[type="radio"]:checked::before {
  background-color: #ae6a65;
  border: 2px solid #ae6a65;
  width: 8px;
  height: 8px;
}
input[type="radio"]:checked {
  background-color: #f4eae9;
  border: 2px solid #ae6a65;
  border-radius: 50%;
  width: 1.3em;
  height: 1.3em;
  outline: none;

  position: relative;
}
input[type="radio"]:disabled {
  cursor: default;
}
</style>
